/**
 * Bulk action utility functions for batch processing extraction jobs.
 * Provides concurrency-controlled batch execution and eligibility helpers.
 */

export const BATCH_SIZE = 5;

/**
 * Processes eligible jobs in sequential batches with concurrency control.
 * Uses Promise.allSettled so individual failures don't halt the operation.
 *
 * @param {Array} eligibleJobs - Jobs to process (each must have `id` and `fileName`)
 * @param {Function} apiCall - Single-job API function: (jobId, reason?) => Promise
 * @param {string} [reason] - Optional reason string (used by markAsUnprocessable)
 * @param {Function} [onProgress] - Callback after each batch: ({ processed, total }) => void
 * @returns {Promise<{ success: number, failed: number, errors: Array<{ jobId: string, fileName: string, error: string }> }>}
 */
export const processBatch = async (eligibleJobs, apiCall, reason, onProgress) => {
  let processed = 0;
  const results = { success: 0, failed: 0, errors: [] };

  for (let i = 0; i < eligibleJobs.length; i += BATCH_SIZE) {
    const batch = eligibleJobs.slice(i, i + BATCH_SIZE);
    const promises = batch.map((job) => apiCall(job.id, reason));
    const settled = await Promise.allSettled(promises);

    settled.forEach((result, idx) => {
      processed++;
      if (result.status === 'fulfilled') {
        results.success++;
      } else {
        results.failed++;
        results.errors.push({
          jobId: batch[idx].id,
          fileName: batch[idx].fileName,
          error:
            result.reason?.response?.data?.message ||
            result.reason?.message ||
            'Unknown error',
        });
      }
    });

    if (onProgress) {
      onProgress({ processed, total: eligibleJobs.length });
    }
  }

  return results;
};

/**
 * Filters selected items to only those eligible for the given action.
 *
 * @param {Array} selectedItems - Array of selected job objects
 * @param {Function} eligibilityFilter - (item) => boolean
 * @returns {Array} Items that pass the eligibility filter
 */
export const getEligibleJobs = (selectedItems, eligibilityFilter) => {
  return selectedItems.filter((item) => eligibilityFilter(item));
};

/**
 * Returns items that are NOT eligible for the given action, with reason strings.
 *
 * @param {Array} selectedItems - Array of selected job objects
 * @param {Function} eligibilityFilter - (item) => boolean
 * @param {Function} statusDisplayFn - (item) => string - returns a human-readable reason
 * @returns {Array<{ job: Object, reason: string }>} Ineligible items with reasons
 */
export const getIneligibleJobs = (selectedItems, eligibilityFilter, statusDisplayFn) => {
  return selectedItems
    .filter((item) => !eligibilityFilter(item))
    .map((item) => ({
      job: item,
      reason: statusDisplayFn(item),
    }));
};
